import { Auth } from '@aws-amplify/auth'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { AxiosError } from 'axios'

import { getCustomerApiClient } from 'api/CustomerApiClient'
import { toast } from 'components/ui/toaster'
import AuthUtils from 'utils/AuthUtils'

import { i18n } from '../i18n/config'

const changePassword = async ({
  oldPassword,
  newPassword
}: {
  oldPassword: string
  newPassword: string
}): Promise<'SUCCESS'> => {
  const user: CognitoUser = await Auth.currentAuthenticatedUser()

  // Let the password change error propagate so the caller can surface it to the user.
  const authResp = await Auth.changePassword(user, oldPassword, newPassword)

  // Best-effort activity log — the password has already changed, so a failure here
  // must not surface to the user as a password-change error.
  try {
    await getCustomerApiClient().createCustomEntityActivity(undefined, {
      type: 'PortalUserResetPassword'
    })
  } catch {
    // ignore
  }

  return authResp
}

const changeEmail = async ({
  email,
  password
}: {
  email: string
  password: string
}): Promise<
  | 'SUCCESS'
  | 'EMAIL_ALREADY_EXISTS'
  | 'INCORRECT_PASSWORD'
  | 'REQUIRE_STRONG_PASSWORD'
  | 'EMAIL_IS_SAME'
  | string
> => {
  try {
    await getCustomerApiClient().updatePortalUserEmail(undefined, {
      email,
      password
    })

    return 'SUCCESS'
  } catch (error) {
    const errorMessage = error.response?.data?.error

    if (
      errorMessage === 'Email already exists, please provide a different email.'
    ) {
      return 'EMAIL_ALREADY_EXISTS'
    }

    if (errorMessage === 'Incorrect password') {
      return 'INCORRECT_PASSWORD'
    }

    if (
      errorMessage ===
      'We require a strong password. Please update your current password and try again.'
    ) {
      return 'REQUIRE_STRONG_PASSWORD'
    }

    if (errorMessage === 'Old and new email should not be same') {
      return 'EMAIL_IS_SAME'
    }

    return error.message
  }
}

const signOut = async (params?: {
  ssoProvider: string
  ssoState: string
  ssoCodeVerifier: string
  isLoginAs?: boolean
}) => {
  const { ssoProvider, ssoState, ssoCodeVerifier, isLoginAs } = params || {}
  const showErrorIfUnexpected = (error: Error) => {
    const isExpectedError =
      error.message?.includes('revoked') ||
      (error as AxiosError)?.response?.status === 401

    if (!isExpectedError) {
      toast.error({
        title: i18n.t('common.error.something_went_wrong'),
        description: error.message
      })
    }
  }

  try {
    const token = await AuthUtils.getRefreshToken()

    if (token) {
      // Used to revoke token
      await getCustomerApiClient().revokeToken(null, { refresh_token: token })
    }

    // Check if we need to do SSO logout first
    if (ssoProvider && ssoState && ssoCodeVerifier) {
      // Do SSO logout first, which may redirect to provider
      const didRedirect = ssoSignOut({
        ssoProvider,
        ssoState,
        ssoCodeVerifier,
        isLoginAs
      })

      if (didRedirect) {
        // Set a flag to indicate we're in the middle of SSO logout
        localStorage.setItem('sso_logout_in_progress', 'true')
        // Don't call AuthUtils.signOut() here - let the SSO redirect handle it
      } else {
        // SSO provider doesn't support logout redirect, clean up local state
        await AuthUtils.signOut()
      }
    } else {
      // No SSO logout needed, clean up local state immediately
      await AuthUtils.signOut()
    }
  } catch (error) {
    console.warn('Error during sign out:', error)

    showErrorIfUnexpected(error)
  }
}

const ssoSignOut = ({
  ssoProvider,
  ssoState,
  ssoCodeVerifier,
  isLoginAs
}: {
  ssoProvider: string
  ssoState: string
  ssoCodeVerifier: string
  isLoginAs?: boolean
}): boolean => {
  const providerItem = JSON.parse(ssoProvider || '{}')

  const logoutUri = providerItem?.oidc_config?.metadata?.logout_uri
  const skipLoginAsLogoutRedirect =
    providerItem?.oidc_config?.metadata?.skip_login_as_logout

  // Skip SSO logout redirect for login-as flow if provider doesn't support it
  if (isLoginAs && skipLoginAsLogoutRedirect) {
    console.log('skipping logout redirect for login-as flow')

    return false
  }

  if (logoutUri && ssoState && ssoCodeVerifier) {
    // redirect to provider's logout_uri
    const urlToNavigate = `${logoutUri}?client_id=${
      providerItem.oidc_config.client_id
    }&post_logout_redirect_uri=${
      providerItem.oidc_config.metadata.logout_redirect_uri
    }&response_type=code&scope=${providerItem.oidc_config.scope
      .split(' ')
      .join(
        '+'
      )}&state=${ssoState}&code_challenge=${ssoCodeVerifier}&code_challenge_method=S256`

    window.location.href = urlToNavigate

    return true
  }

  return false
}

const completeSSOLogout = async () => {
  try {
    // Complete the local logout
    await AuthUtils.signOut()
    // Clear the SSO logout flag
    localStorage.removeItem('sso_logout_in_progress')
  } catch (error) {
    console.warn('Error completing SSO logout:', error)
    // Clear the flag anyway
    localStorage.removeItem('sso_logout_in_progress')
  }
}

export { changePassword, changeEmail, signOut, completeSSOLogout }
